<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->

<mat-card appearance="outlined" id="card-results">
  <mat-card-header>
    <mat-card-title>Results</mat-card-title>
  </mat-card-header>
  <mat-card-content>
    <div>
      <div class="info-stats-container">
        <div class="info-stat-card">
          <span class="info-stat-label">Items Used</span>
          <span class="info-stat-value">{{ this.itemCount | number }}</span>
        </div>
        <div class="info-stat-card">
          <span class="info-stat-label">Results shown</span>
          <div class="info-stat-value-container">
            <span class="info-stat-value">{{ this.savedResults | number }}</span>
            <mat-icon
              #tooltip="matTooltip"
              *ngIf="this.resultLimitReached"
              class="report-problem-icon"
              matTooltip="Note: To speed up the whole process and prevent Out-Of-Memory crashes, only {{
                this.savedResults | number
              }} results are listed in this table.
                          If you need more entries, disable the limitation in the advanced settings."
              >report_problem
            </mat-icon>
          </div>
        </div>
        <div class="info-stat-card">
          <span class="info-stat-label">Combinations calculated</span>
          <div class="info-stat-value-container">
            <span class="info-stat-value">{{ this.totalPermutations | number }}</span>
            <mat-icon
              #tooltip="matTooltip"
              *ngIf="this.stoppedEarlyAtMaxTier"
              class="report-problem-icon"
              matTooltip="The calculation stopped early because the maximum possible stat value of 200 was found for every stat. The remaining combinations did not need to be checked."
              >report_problem
            </mat-icon>
            <mat-icon
              #tooltip="matTooltip"
              *ngIf="this.cancelledCalculation"
              class="report-problem-icon"
              matTooltip="The calculation was cancelled before all combinations were checked, so this count is incomplete."
              >report_problem
            </mat-icon>
          </div>
        </div>
        <!-- <div class="info-stat-card">
          <span class="info-stat-label">Combinations possible</span>
          <span class="info-stat-value">{{ this.totalPossibleCombinations | number }}</span>
        </div> -->
        <div class="info-stat-card">
          <span class="info-stat-label">Time required</span>
          <div class="info-stat-value-container">
            <span class="info-stat-value">{{ this.totalTime | number }}ms</span>
            <mat-spinner
              *ngIf="isCalculatingPermutations"
              diameter="16"
              aria-label="Calculation still in progress"></mat-spinner>
          </div>
        </div>
        <div class="view-mode-toggle">
          <mat-button-toggle-group
            [value]="viewMode"
            (change)="onViewModeChange($event)"
            aria-label="View Mode">
            <mat-button-toggle value="cards">
              <mat-icon>view_module</mat-icon>
              Cards
            </mat-button-toggle>
            <mat-button-toggle value="table">
              <mat-icon>table_view</mat-icon>
              Table
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      </div>

      <div class="config-summary-container">
        <h4 class="config-summary-title">Summary of important configuration choices</h4>
        <mat-chip-listbox
          aria-label="Configuration Summary"
          class="config-summary-chips"
          [selectable]="false">
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_selectedExotics.indexOf(-1) > -1"
            disableRipple
            matTooltip="This setting enforces that all exotics are ignored."
            >No Exotic
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              _config_selectedExotics.length >= 1 && _config_selectedExotics.indexOf(-1) === -1
            "
            disableRipple
            matTooltip="This setting enforces that only one specific exotic is used."
            >Exotic
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_tryLimitWastedStats"
            disableRipple
            matTooltip="The tool will add minor stat mods to reduce wasted stats."
            >Reduce wasted stats
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_modslotLimitation"
            disableRipple
            matTooltip="This setting limits available stat mod types, like major Super or Class."
            >Stat Mod Limitation
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_armorPerkLimitation"
            disableRipple
            matTooltip="This setting enforces an specific armor perk or modslot for a specific armor slot.">
            Armor Perk or Slot
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyShowResultsWithNoWastedStats"
            disableRipple
            matTooltip="This setting means that only builds with no wasted stats are shown."
            selected
            >Zero Waste
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              (_config_assumeEveryLegendaryIsArtifice && _config_legacyArmor) ||
              _config_assumeEveryExoticIsArtifice
            "
            disableRipple
            matTooltip="Some legacy items are assumed to have an artifice slot."
            color="warn">
            <mat-icon inline style="height: 100%">report_problem</mat-icon>
            &nbsp;Legacy Artifice Assumptions&nbsp;
            <mat-icon inline style="height: 100%">report_problem</mat-icon>
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyUseMasterworkedExotics"
            disableRipple
            matTooltip="This setting means that only exotic armor pieces that are already masterworked are used."
            selected>
            Masterworked Exotics Only
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_onlyUseMasterworkedLegendaries"
            disableRipple
            matTooltip="This setting means that only legendary armor pieces are already masterworked are used."
            selected>
            Masterworked Legendaries Only
          </mat-chip-option>

          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="
              !_config_onlyUseMasterworkedExotics &&
              !_config_onlyUseMasterworkedLegendaries &&
              (_config_assumeLegendariesMasterworked || _config_assumeExoticsMasterworked)
            "
            disableRipple
            matTooltip="Some masterwork assumptions are in place. This means that you may have to masterwork items. Look at your advanced settings to see which ones are activated.">
            Masterwork Assumption
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_enforceFeaturedArmor"
            disableRipple
            matTooltip="This setting enforces that only featured armor pieces (from specific activities or vendors) are used in builds."
            selected>
            Featured Armor Only
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_includeCollectionRolls"
            disableRipple
            matTooltip="Collection Exotic rolls will be included in the search.">
            Include Collection Rolls
          </mat-chip-option>
          <mat-chip-option
            #tooltip="matTooltip"
            *ngIf="_config_includeVendorRolls"
            disableRipple
            matTooltip="Currently available Vendor items will be included in the search.">
            Include Vendor Items
          </mat-chip-option>
        </mat-chip-listbox>
      </div>

      <div class="initializing-container" *ngIf="initializing">
        <mat-icon class="rotate">hourglass_top</mat-icon>
        <h3>Initializing...</h3>
        <p>Please wait while the application is loading your data.</p>
        <p>
          This may take a while, depending on your settings and the number of items you have.
          <br />
          Please be patient.
        </p></div
      >

      <div
        class="progress-bar-container"
        *ngIf="_results.length === 0 && isCalculatingPermutations && !initializing">
        <mat-icon class="rotate"> hourglass_top </mat-icon>
        <h3>Calculating permutations...</h3>
        <mat-progress-bar
          [value]="computationProgress"
          aria-label="Calculating permutations..."></mat-progress-bar>
        <p>
          This may take a while, depending on your settings and the number of items you have.
          <br />
          Please be patient.
        </p>

        <br />
        <button
          mat-raised-button
          color="warn"
          matTooltip="Cancel the current calculation. This is useful if you want to change your settings or if the calculation takes too long."
          (click)="cancelCalculation()"
          [disabled]="!isCalculatingPermutations">
          Cancel Calculation
        </button>
      </div>

      <!-- Ongoing calculation progress while results are already visible -->
      <div
        class="progress-bar-container"
        *ngIf="_results.length > 0 && isCalculatingPermutations && !initializing">
        <mat-icon class="rotate"> hourglass_top </mat-icon>
        <h3>Continuing calculation...</h3>
        <mat-progress-bar
          [value]="computationProgress"
          aria-label="Continuing calculation..."></mat-progress-bar>
        <p>
          Additional combinations are still being checked. Current results are shown while the
          calculation continues.
        </p>

        <br />
        <button
          mat-raised-button
          color="warn"
          matTooltip="Cancel the current calculation. This is useful if you want to change your settings or if the calculation takes too long."
          (click)="cancelCalculation()"
          [disabled]="!isCalculatingPermutations">
          Cancel Calculation
        </button>
      </div>

      <!-- Cancelled Message -->
      <div class="cancelled-message" *ngIf="_results.length === 0 && cancelledCalculation">
        <mat-icon>cancel</mat-icon>
        <h3>You cancelled the calculation process</h3>
        <p>
          The calculation has been cancelled. You can try again with different settings.
          <br />
          If you want to continue, just change anything in your settings.
        </p>
      </div>

      <!-- No results message -->
      <div
        class="no-results"
        *ngIf="
          _results.length === 0 &&
          !isCalculatingPermutations &&
          !initializing &&
          !cancelledCalculation
        ">
        <mat-icon>search_off</mat-icon>
        <h3>No results found</h3>
        <p>Try adjusting your filters or search criteria.</p>
        <div id="common-issues">
          <p>Common issues:</p>
          <ul>
            <li> Desired stats may simply not be possible with your current configuration. </li>
            <li>Make sure that you have at least one legendary item per slot.</li>
            <li *ngIf="_config_enforceFeaturedArmor">
              You enforced that only featured armor is used. Do you have at least one piece of
              featured armor for each slot?
            </li>
            <li *ngIf="!_config_legacyArmor">
              Maybe you wanted to include legacy (Armor 2.0) armor too?
            </li>
            <li *ngIf="!_config_includeVendorRolls"> Maybe you wanted to include vendor items? </li>
            <li *ngIf="!_config_includeCollectionRolls">
              Maybe you wanted to include exotics in the collection?
            </li>
          </ul>
        </div>
      </div>

      <!-- Results Content -->
      <div *ngIf="_results.length > 0">
        <!-- Warning when results are shown after cancellation -->
        <div class="cancelled-warning-banner" *ngIf="cancelledCalculation">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>Calculation was cancelled.</strong>
            <span>
              These results might be incomplete. The possible max stats might be inaccurate</span
            >
          </div>
        </div>
        <!-- Card View -->
        <app-results-card-view
          *ngIf="viewMode === 'cards'"
          [results]="_results"
          [class.loading-blur]="(status.status | async)?.updatingResultsTable">
        </app-results-card-view>

        <!-- Table View -->
        <app-results-table-view
          *ngIf="viewMode === 'table'"
          [results]="_results"
          [class.loading-blur]="(status.status | async)?.updatingResultsTable">
        </app-results-table-view>
      </div>
    </div>
  </mat-card-content>
  <mat-card-actions *ngIf="savedResults > 0">
    <span class="flex-spacer"></span>

    <button mat-raised-button color="primary" (click)="saveBuilds()">
      <mat-icon>file_download</mat-icon>
      Download results as JSON
    </button>
  </mat-card-actions>
</mat-card>
